import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { AlertTriangle, X, ChevronDown } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LEVELS = {
  1: { label: "Stufe 1 · Gelb", cls: "bg-[#E9C46A] text-[#1A251E]", icon: "#1A251E" },
  2: { label: "Stufe 2 · Orange", cls: "bg-[#D97E2B] text-white", icon: "#fff" },
  3: { label: "Stufe 3 · Rot", cls: "bg-[#B93829] text-white", icon: "#fff" },
  4: { label: "Stufe 4 · Extrem", cls: "bg-[#5B1B2E] text-white", icon: "#fff" },
};

const fmt = (ms) =>
  ms
    ? new Date(ms).toLocaleString("de-DE", {
        weekday: "short", day: "2-digit", month: "2-digit",
        hour: "2-digit", minute: "2-digit", timeZone: "Europe/Berlin",
      }) + " Uhr"
    : "";

export default function WeatherWarnings() {
  const [warnings, setWarnings] = useState(null);
  const [dismissed, setDismissed] = useState(() =>
    JSON.parse(sessionStorage.getItem("kga_warn_dismissed") || "[]")
  );
  const [openIdx, setOpenIdx] = useState(null);

  useEffect(() => {
    fetch(`${API}/warnings`)
      .then((r) => r.json())
      .then((d) => setWarnings(d.warnings || []))
      .catch(() => setWarnings([]));
  }, []);

  if (!warnings) return null;
  const active = warnings.filter((w) => !dismissed.includes(`${w.headline}|${w.start}`));
  if (active.length === 0) return null;

  const maxLevel = Math.max(...active.map((w) => w.level || 0), 1);
  const lvl = LEVELS[maxLevel] || LEVELS[2];

  const dismiss = (w) => {
    const key = `${w.headline}|${w.start}`;
    const next = [...dismissed, key];
    setDismissed(next);
    sessionStorage.setItem("kga_warn_dismissed", JSON.stringify(next));
  };

  return (
    <div className="pt-24 md:pt-28 px-4 sm:px-6 lg:px-8" data-testid="weather-warning-banner">
      <motion.div
        initial={{ opacity: 0, y: -16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        className={`mx-auto max-w-7xl rounded-2xl shadow-[0_18px_40px_rgba(185,56,41,0.25)] ${lvl.cls}`}
      >
        <div className="px-5 sm:px-7 py-5">
          <div className="flex items-center gap-3 mb-3">
            <AlertTriangle size={22} color={lvl.icon} className="animate-float" />
            <p className="font-jbm text-xs tracking-[0.25em] uppercase font-semibold">
              Amtliche Wetterwarnung des DWD · Berlin · {lvl.label}
            </p>
          </div>
          <div className="space-y-3">
            {active.map((w, i) => (
              <div key={`${w.headline}-${i}`} className="rounded-xl bg-black/10 px-4 py-3" data-testid={`warning-item-${i}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="font-display text-lg sm:text-xl font-bold leading-snug">{w.headline}</p>
                    <p className="mt-1 font-jbm text-xs tracking-wider opacity-80">
                      {w.kind === "vorab" ? "Vorabinformation · " : ""}
                      {fmt(w.start)} – {fmt(w.end)}
                    </p>
                  </div>
                  <div className="flex shrink-0 gap-2">
                    {(w.description || w.instruction) && (
                      <button
                        onClick={() => setOpenIdx(openIdx === i ? null : i)}
                        data-testid={`warning-details-${i}`}
                        aria-label="Details anzeigen"
                        className="flex h-8 w-8 items-center justify-center rounded-full bg-black/15 transition-transform hover:scale-110"
                      >
                        <ChevronDown size={15} className={`transition-transform duration-300 ${openIdx === i ? "rotate-180" : ""}`} />
                      </button>
                    )}
                    <button
                      onClick={() => dismiss(w)}
                      data-testid={`warning-dismiss-${i}`}
                      aria-label="Warnung ausblenden"
                      className="flex h-8 w-8 items-center justify-center rounded-full bg-black/15 transition-transform hover:scale-110"
                    >
                      <X size={15} />
                    </button>
                  </div>
                </div>
                <AnimatePresence>
                  {openIdx === i && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3 }}
                      className="overflow-hidden"
                    >
                      {w.description && (
                        <p className="mt-3 text-sm leading-relaxed opacity-95" data-testid={`warning-description-${i}`}>{w.description}</p>
                      )}
                      {w.instruction && (
                        <p className="mt-2 text-sm font-semibold leading-relaxed" data-testid={`warning-instruction-${i}`}>
                          Hinweis: {w.instruction}
                        </p>
                      )}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ))}
          </div>
        </div>
      </motion.div>
    </div>
  );
}
